import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  findSegmentAt,
  getMentionTriggerPosition,
  parseEditable,
  renderedOffsetToMarkdownOffset,
  markdownOffsetToRenderedOffset,
} from './mdEditable';
import { LinkTooltip } from './LinkTooltip';
import { AddLinkModal } from './AddLinkModal';
import { MentionMenu } from './MentionMenu';
import { insertLinkAtSelection } from './insertMarkdownLink';

export type MentionConfig = {
  recognizedTokens: readonly string[];
  insertToken: string;
  menuTitle: string;
  menuSubtitle: string;
};

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  mention?: MentionConfig;
};
type StyledProps = { placeholder?: string };
type Props = MarkdownEditorProps & StyledProps;
export type MarkdownEditorHandle = {
  openAddLink: () => void;
};
const sanitizeUrl = (url: string): string => {
  const trimmed = url.trim();
  const allowed = ['http:', 'https:', 'mailto:', 'tel:'];
  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed);
  if (hasScheme && !allowed.some((s) => trimmed.startsWith(s))) {
    return '';
  }
  if (trimmed.startsWith('javascript:') || trimmed.startsWith('data:')) {
    return '';
  }
  return trimmed;
};
export const getCaretOffset = (container: HTMLElement): number | null => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (!container.contains(range.startContainer)) return null;

  const preRange = range.cloneRange();
  preRange.selectNodeContents(container);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length;
};

const getSelectionOffsets = (
  container: HTMLElement,
): {
  start: number;
  end: number;
} | null => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (
    !container.contains(range.startContainer) ||
    !container.contains(range.endContainer)
  )
    return null;

  const startRange = range.cloneRange();
  startRange.selectNodeContents(container);
  startRange.setEnd(range.startContainer, range.startOffset);
  const start = startRange.toString().length;

  const endRange = range.cloneRange();
  endRange.selectNodeContents(container);
  endRange.setEnd(range.endContainer, range.endOffset);
  const end = endRange.toString().length;

  return { start, end };
};

const setCaretOffset = (container: HTMLElement, offset: number) => {
  const selection = window.getSelection();
  if (!selection) return;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null,
  );

  let remaining = offset;
  let currentNode = walker.nextNode() as Text | null;

  while (currentNode) {
    const length = currentNode.textContent?.length ?? 0;
    if (remaining <= length) {
      if (
        remaining === length &&
        currentNode.parentElement &&
        (currentNode.parentElement.tagName === 'A' ||
          currentNode.parentElement.dataset.mention === 'true')
      ) {
        const range = document.createRange();
        const nextTextSibling = currentNode.parentElement.nextSibling;
        if (nextTextSibling && nextTextSibling.nodeType === Node.TEXT_NODE) {
          range.setStart(nextTextSibling, 0);
        } else {
          range.setStartAfter(currentNode.parentElement);
        }
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }

      const range = document.createRange();
      range.setStart(currentNode, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= length;
    currentNode = walker.nextNode() as Text | null;
  }

  const range = document.createRange();
  range.selectNodeContents(container);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const serializeContent = (container: HTMLElement): string => {
  const parts: string[] = [];

  container.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? '');
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === 'A') {
        const anchor = el as HTMLAnchorElement;
        const label = anchor.textContent ?? '';
        const href = anchor.getAttribute('href') ?? '';
        parts.push(`[${label}](${href})`);
        return;
      }

      if (el.dataset.mention === 'true') {
        parts.push(el.textContent ?? '');
        return;
      }

      if (el.childNodes.length > 0) {
        parts.push(serializeContent(el));
      }
    }
  });

  return parts.join('');
};

const getMentionAnchorPosition = (
  container: HTMLElement,
): { top: number; left: number } => {
  const selection = window.getSelection();
  const range = selection?.rangeCount
    ? selection.getRangeAt(0).cloneRange()
    : null;
  const rect = range ? range.getBoundingClientRect() : null;
  const containerRect = container.getBoundingClientRect();

  if (rect) {
    return {
      top: rect.bottom - containerRect.top + 6,
      left: rect.left - containerRect.left,
    };
  }

  return { top: 12, left: 12 };
};

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, Props>(
  (
    {
      value,
      onChange,
      placeholder = 'Tell us something...',
      disabled = false,
      mention,
    }: Props,
    ref,
  ) => {
    const mentionTokens = mention?.recognizedTokens;
    const editableValue = useMemo(
      () => parseEditable(value, mentionTokens ?? []),
      [value, mentionTokens],
    );
    const caretOffsetRef = useRef<number | null>(null);
    const caretSeqRef = useRef(0);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const activeLinkRef = useRef<typeof activeLink>(null);
    const hasFocusedOnceRef = useRef(false);
    const [activeLink, setActiveLink] = useState<{
      label: string;
      href: string;
      start: number;
      end: number;
      top: number;
      left: number;
      reason: 'hover' | 'cursor' | 'click';
    } | null>(null);
    const [, setIsPointerInTooltip] = useState(false);
    const [linkModal, setLinkModal] = useState<{
      mode: 'add' | 'edit';
      start: number;
      end: number;
      label: string;
      href: string;
    } | null>(null);
    const [mentionPrompt, setMentionPrompt] = useState<{
      start: number;
      end: number;
      anchorTop: number;
      anchorLeft: number;
    } | null>(null);
    const pendingFocusRef = useRef(false);

    const storeCaretOffset = useCallback((target: HTMLDivElement) => {
      caretOffsetRef.current = getCaretOffset(target);
      caretSeqRef.current += 1;
    }, []);

    const refreshMentionPrompt = useCallback(
      (container: HTMLElement, text: string) => {
        if (!mention) return;
        const caretRendered = getCaretOffset(container);
        if (caretRendered === null) {
          setMentionPrompt(null);
          return;
        }
        const segments = parseEditable(text, mention.recognizedTokens);
        const caretMd = renderedOffsetToMarkdownOffset(segments, caretRendered);
        const triggerStart = getMentionTriggerPosition(
          text,
          caretMd,
          mention.recognizedTokens,
        );
        if (triggerStart >= 0) {
          const anchor = getMentionAnchorPosition(container);
          setMentionPrompt({
            start: triggerStart,
            end: caretMd,
            anchorTop: anchor.top,
            anchorLeft: anchor.left,
          });
        } else {
          setMentionPrompt(null);
        }
      },
      [mention],
    );

    const handleInput = useCallback(
      (event: React.FormEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        storeCaretOffset(target);
        const serialized = serializeContent(target);

        onChange(serialized);
        refreshMentionPrompt(target, serialized);
      },
      [onChange, refreshMentionPrompt, storeCaretOffset],
    );

    const handleSelectionChange = useCallback(() => {
      if (!containerRef.current) return;
      storeCaretOffset(containerRef.current);
      if (mention) {
        refreshMentionPrompt(containerRef.current, value);
      }
    }, [mention, refreshMentionPrompt, storeCaretOffset, value]);

    const insertMention = useCallback(
      (promptStart: number, promptEnd: number) => {
        if (!mention) return;
        const insertion = `${mention.insertToken} `;
        const newValue =
          value.slice(0, promptStart) + insertion + value.slice(promptEnd);
        const newSegments = parseEditable(newValue, mention.recognizedTokens);
        caretOffsetRef.current = markdownOffsetToRenderedOffset(
          newSegments,
          promptStart + insertion.length,
        );
        setMentionPrompt(null);
        onChange(newValue);
      },
      [mention, onChange, value],
    );

    const handleMentionKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!mention) return;
        if (!mentionPrompt && event.key === '@' && containerRef.current) {
          const container = containerRef.current;
          const caretRendered =
            getCaretOffset(container) ?? container.textContent?.length ?? 0;
          const caretMd = renderedOffsetToMarkdownOffset(
            editableValue,
            caretRendered,
          );
          const anchor = getMentionAnchorPosition(container);
          setMentionPrompt({
            start: caretMd,
            end: caretMd,
            anchorTop: anchor.top,
            anchorLeft: anchor.left,
          });
          return;
        }
        if (!mentionPrompt) return;
        if (event.key === 'Enter') {
          event.preventDefault();
          insertMention(mentionPrompt.start, mentionPrompt.end);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setMentionPrompt(null);
        }
      },
      [editableValue, insertMention, mention, mentionPrompt],
    );

    const handleMentionSelect = useCallback(() => {
      if (!mentionPrompt) return;
      insertMention(mentionPrompt.start, mentionPrompt.end);
    }, [insertMention, mentionPrompt]);

    const showLinkDetails = useCallback(
      (anchor: HTMLAnchorElement, reason: 'click') => {
        if (!containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const rect = anchor.getBoundingClientRect();
        const label = anchor.textContent ?? '';
        const href = anchor.getAttribute('href') ?? '';
        const start = Number(anchor.dataset.mdStart ?? '-1');
        const end = Number(anchor.dataset.mdEnd ?? '-1');

        setActiveLink({
          label,
          href,
          start,
          end,
          top: rect.bottom - containerRect.top + 8,
          left: rect.left - containerRect.left,
          reason,
        });
      },
      [],
    );

    const handleFocus = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;
      const textLength = container.textContent?.length ?? 0;
      if (pendingFocusRef.current && caretOffsetRef.current !== null) {
        const target = Math.min(caretOffsetRef.current, textLength);
        hasFocusedOnceRef.current = true;
        requestAnimationFrame(() => {
          setCaretOffset(container, target);
        });
        return;
      }
      const offset = hasFocusedOnceRef.current
        ? getCaretOffset(container) ?? textLength
        : textLength;
      caretOffsetRef.current = offset;
      requestAnimationFrame(() => {
        setCaretOffset(container, offset);
      });
      hasFocusedOnceRef.current = true;
    }, []);

    const handleRemoveLink = useCallback(() => {
      const link = activeLinkRef.current ?? activeLink;
      if (!link) return;
      const newValue = value.slice(0, link.start) + value.slice(link.end);
      caretOffsetRef.current = link.start;
      setActiveLink(null);
      setIsPointerInTooltip(false);
      onChange(newValue);
    }, [activeLink, onChange, value]);

    const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
      const rt = e.relatedTarget as HTMLElement | null;
      if (rt?.dataset?.linkTooltip === 'true') return;
      setActiveLink(null);
      setIsPointerInTooltip(false);
      setMentionPrompt(null);
    }, []);

    const handleEditLink = useCallback(() => {
      const link = activeLinkRef.current ?? activeLink;
      if (!link) return;
      setActiveLink(null);
      setIsPointerInTooltip(false);
      setLinkModal({
        mode: 'edit',
        start: link.start,
        end: link.end,
        label: link.label,
        href: link.href,
      });
    }, [activeLink]);

    useLayoutEffect(() => {
      activeLinkRef.current = activeLink;
    }, [activeLink]);

    const handleLinkModalSave = useCallback(
      ({ name, href }: { name: string; href: string }) => {
        if (!linkModal) return;
        const start = Math.max(0, Math.min(linkModal.start, value.length));
        const end = Math.max(start, Math.min(linkModal.end, value.length));
        const res = insertLinkAtSelection(
          value,
          { start, end },
          { name, href },
        );
        if (!res.allowed) {
          setLinkModal(null);
          return;
        }
        const newSegments = parseEditable(res.value);
        const renderedCaret = markdownOffsetToRenderedOffset(
          newSegments,
          res.caretOffset,
        );
        caretOffsetRef.current = renderedCaret;
        pendingFocusRef.current = true;
        setLinkModal(null);
        setActiveLink(null);
        onChange(res.value);
        requestAnimationFrame(() => {
          const target = containerRef.current;
          if (!target || caretOffsetRef.current === null) return;
          target.focus();
          setCaretOffset(
            target,
            Math.min(caretOffsetRef.current, target.textContent?.length ?? 0),
          );
          pendingFocusRef.current = false;
        });
        setTimeout(() => {
          const target = containerRef.current;
          if (!target || caretOffsetRef.current === null) return;
          target.focus();
          setCaretOffset(
            target,
            Math.min(caretOffsetRef.current, target.textContent?.length ?? 0),
          );
          pendingFocusRef.current = false;
        }, 0);
      },
      [linkModal, onChange, value],
    );

    const openAddLink = useCallback(() => {
      if (!containerRef.current) return;
      const selectionInfo = getSelectionOffsets(containerRef.current);
      const renderedStart = selectionInfo?.start ?? selectionInfo?.end ?? 0;
      const renderedEnd =
        selectionInfo?.end ??
        getCaretOffset(containerRef.current) ??
        renderedStart;

      const mdStart = renderedOffsetToMarkdownOffset(
        editableValue,
        renderedStart,
      );
      const mdEnd = renderedOffsetToMarkdownOffset(editableValue, renderedEnd);
      const normalizedStart = Math.min(mdStart, mdEnd);
      const normalizedEnd = Math.max(mdStart, mdEnd);

      const seg = findSegmentAt(editableValue, normalizedStart);
      if (!seg || seg.type !== 'text' || normalizedEnd > seg.end) {
        return;
      }

      const label = value.slice(normalizedStart, normalizedEnd);
      setLinkModal({
        mode: 'add',
        start: normalizedStart,
        end: normalizedEnd,
        label,
        href: '',
      });
    }, [editableValue, value]);

    useImperativeHandle(ref, () => ({ openAddLink }), [openAddLink]);

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const shouldRestoreCaret =
        document.activeElement === container && caretOffsetRef.current !== null;
      const expectedSeq = caretSeqRef.current;

      const fragment = document.createDocumentFragment();
      editableValue.forEach((v) => {
        if (v.type === 'text') {
          fragment.appendChild(document.createTextNode(v.value));
        }
        if (v.type === 'mention') {
          const mentionSpan = document.createElement('span');
          mentionSpan.textContent = v.value;
          mentionSpan.dataset.mention = 'true';
          mentionSpan.className =
            'rounded-[3px] bg-[#EDE5FC] px-0.5 text-[#5F1ECC]';
          fragment.appendChild(mentionSpan);
        }
        if (v.type === 'link') {
          const anchor = document.createElement('a');
          anchor.href = sanitizeUrl(v.value.href) || '#';
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
          anchor.textContent = v.value.name;
          anchor.className =
            'text-base font-medium leading-6 text-primary-800 underline';
          anchor.dataset.mdStart = String(v.start);
          anchor.dataset.mdEnd = String(v.end);
          anchor.onclick = (e) => {
            e.preventDefault();
            showLinkDetails(anchor, 'click');
          };
          anchor.onmousedown = (e) => e.preventDefault();
          anchor.onmouseup = (e) => e.preventDefault();
          fragment.appendChild(anchor);
        }
      });

      container.replaceChildren(fragment);

      const totalLength = container.textContent?.length ?? 0;
      const targetOffset = Math.min(caretOffsetRef.current ?? 0, totalLength);

      if (pendingFocusRef.current && caretOffsetRef.current !== null) {
        container.focus();
        setCaretOffset(container, targetOffset);
        pendingFocusRef.current = false;
        return;
      }

      if (!shouldRestoreCaret) return;
      if (expectedSeq !== caretSeqRef.current) return;

      setCaretOffset(container, targetOffset);
    }, [editableValue, showLinkDetails]);

    return (
      <div className="relative" onBlur={handleBlur}>
        <div
          ref={containerRef}
          className={`min-h-[100px] w-full whitespace-pre-wrap [overflow-wrap:anywhere] border-none p-2 text-base font-normal leading-6 text-[#131313] caret-primary-800 outline-none focus:shadow-none focus:outline-none focus-visible:outline-none${
            disabled ? ' pointer-events-none opacity-60' : ''
          }`}
          contentEditable={!disabled}
          role="textbox"
          aria-multiline="true"
          suppressContentEditableWarning
          onInput={handleInput}
          onFocus={handleFocus}
          onKeyDown={handleMentionKeyDown}
          onKeyUp={() => {
            handleSelectionChange();
          }}
          onMouseUp={() => {
            handleSelectionChange();
          }}
        />
        {placeholder && !value && (
          <div className="pointer-events-none absolute left-2 top-2 text-base font-normal leading-6 text-[#9E9E9E]">
            {placeholder}
          </div>
        )}
        {mention && mentionPrompt && (
          <MentionMenu
            top={mentionPrompt.anchorTop}
            left={mentionPrompt.anchorLeft}
            title={mention.menuTitle}
            subtitle={mention.menuSubtitle}
            onSelect={handleMentionSelect}
          />
        )}
        {activeLink && (
          <LinkTooltip
            top={activeLink.top}
            left={activeLink.left}
            label={activeLink.label}
            href={activeLink.href}
            onClose={() => setActiveLink(null)}
            onEdit={handleEditLink}
            onRemove={handleRemoveLink}
            onMouseEnter={() => setIsPointerInTooltip(true)}
            onMouseLeave={() => setIsPointerInTooltip(false)}
          />
        )}
        {linkModal && (
          <AddLinkModal
            open
            mode={linkModal.mode}
            initialLabel={linkModal.label}
            initialHref={linkModal.href}
            onClose={() => setLinkModal(null)}
            onSave={handleLinkModalSave}
          />
        )}
      </div>
    );
  },
);

MarkdownEditor.displayName = 'MarkdownEditor';
